import classNames from 'classnames';
import * as Flowbite from 'flowbite-react';
import { ComponentProps, ReactNode, useMemo } from 'react';
import { getReplaceThemeProps } from 'utils/replaceTheme';

const theme = {
    icon: {
        off: '',
    },
    root: {
        base: 'border-y-[0.5px] border-solid border-x-[0.5px]',
        color: {
            blue: 'bg-blue-50 border-blue-200 text-blue-700',
            gray: 'bg-gray-50 border-gray-200 text-gray-500',
            indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
            pink: 'bg-pink-50 border-pink-200 text-pink-700',
            primary: 'bg-primary-50 border-primary-200 text-primary-700',
            teal: 'bg-teal-50 border-teal-200 text-teal-700',
            darkGray: 'bg-gray-200 border-gray-400 text-gray-900',
            red: 'bg-red-100 border-red-400 text-red-800',
            orange: 'bg-orange-100 border-orange-400 text-orange-800',
            yellow: 'bg-yellow-100 border-yellow-400 text-yellow-800',
        },
        size: {
            xs: 'p-0.5 text-2xs/none',
            sm: 'p-1 text-sm/none !font-medium tracking-normal !font-sans', // This setup is for alerts
        },
    },
};

interface DoubleBadgeProps extends Omit<ComponentProps<'span'>, 'color'> {
    color?:
        | 'blue'
        | 'darkGray'
        | 'gray'
        | 'indigo'
        | 'orange'
        | 'pink'
        | 'primary'
        | 'red'
        | 'teal'
        | 'yellow';
    label?: ReactNode;
    statusBadge?: boolean;
    size?: Flowbite.BadgeProps['size'];
}

export const DoubleBadge = ({
    children,
    className,
    color = 'gray',
    label = 'ID',
    ref,
    size = 'xs',
    statusBadge = false,
    ...props
}: DoubleBadgeProps) => {
    const labelTextColor = useMemo(() => {
        switch (color) {
            case 'blue':
                return 'text-blue-500';

            case 'indigo':
                return 'text-indigo-500';

            case 'pink':
                return 'text-pink-500';

            case 'primary':
                return 'text-primary-500';

            case 'teal':
                return 'text-teal-500';

            case 'darkGray':
                return 'text-gray-900';

            case 'red':
                return 'text-red-800';

            case 'orange':
                return 'text-orange-800';

            case 'yellow':
                return 'text-yellow-800';

            default:
                return 'text-gray-400';
        }
    }, [color]);

    return (
        <span
            ref={ref}
            className={classNames('flex-none inline-flex', className)}
            {...props}
        >
            <Flowbite.Badge
                className={classNames(
                    'border-x-[0.5px] font-mono rounded-l-sm rounded-r-none',
                    labelTextColor,
                    statusBadge && 'rounded-l'
                )}
                color={color}
                size={size}
                {...getReplaceThemeProps(theme)}
            >
                {label}
            </Flowbite.Badge>

            <Flowbite.Badge
                className={classNames(
                    'border-l-0 border-r-[0.5px] font-semibold rounded-l-none rounded-r-sm',
                    statusBadge && 'rounded-r'
                )}
                color={color}
                size={size}
                {...getReplaceThemeProps(theme)}
            >
                {children}
            </Flowbite.Badge>
        </span>
    );
};

DoubleBadge.displayName = 'DoubleBadge';
